import React, { useEffect, useState, useRef } from 'react';
import * as awarenessProtocol from 'y-protocols/awareness';
import { useMeQuery } from '../hooks/useMeQuery';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Logger } from '../utils/logger';
import { useTranslation } from '@/hooks/useTranslation';
import { SessionMetrics } from '../domains/analytics/services/SessionMetrics';

const logger = Logger.getLoggerWithPrefix('ConnectedUsers');

interface UserInfo {
  clientId: number;
  name: string;
  color: string;
  avatarUrl?: string;
}

interface ConnectedUsersProps {
  awareness: awarenessProtocol.Awareness | undefined;
}

const CURSOR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#FD79A8', '#FDCB6E', '#6C5CE7'
];

const generateUserName = (clientId: number) => {
  const adjectives = ['Swift', 'Bright', 'Cool', 'Happy', 'Bold'];
  const nouns = ['Fox', 'Eagle', 'Tiger', 'Hawk', 'Wolf'];
  const adj = adjectives[clientId % adjectives.length];
  const noun = nouns[Math.floor(clientId / adjectives.length) % nouns.length];
  return `${adj} ${noun}`;
};

const getInitials = (name: string) => {
  const parts = name.split(' ');
  return parts.map(p => p[0]).join('').toUpperCase();
};

export const ConnectedUsers: React.FC<ConnectedUsersProps> = ({ awareness }) => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [localUser, setLocalUser] = useState<UserInfo | null>(null);
  const { t } = useTranslation('common');
  const { data: me } = useMeQuery();
  const lastSeenRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    if (!awareness) {
      return;
    }

    const localClientId = awareness.clientID;
    const current = awareness.getLocalState() as any;
    // Initialize only once when actual user id is available; avoid changing id later
    if (!current?.user && me?.id) {
      const name = me.name || generateUserName(localClientId);
      const color = CURSOR_COLORS[localClientId % CURSOR_COLORS.length];
      awareness.setLocalStateField('user', { id: me.id, name, color, avatarUrl: me.avatarUrl });
      setLocalUser({ clientId: localClientId, name, color, avatarUrl: me.avatarUrl });
      // Track presence joined (first-time)
      SessionMetrics.firstTime('collaboration.presence_joined');
    } else if (current?.user) {
      setLocalUser({
        clientId: localClientId,
        name: current.user.name,
        color: current.user.color,
        avatarUrl: current.user.avatarUrl,
      });
      // If we already have a user set and API data arrives, update known fields (keep id stable)
      const shouldUpdateName = Boolean(me?.name && current.user.name !== me.name);
      const shouldUpdateAvatar = current.user.avatarUrl !== me?.avatarUrl;

      if ((shouldUpdateName || shouldUpdateAvatar) && current.user.id) {
        awareness.setLocalStateField('user', {
          id: current.user.id,
          name: shouldUpdateName ? me!.name : current.user.name,
          color: current.user.color,
          avatarUrl: me?.avatarUrl,
        });
        setLocalUser({
          clientId: localClientId,
          name: shouldUpdateName ? me!.name : current.user.name,
          color: current.user.color,
          avatarUrl: me?.avatarUrl,
        });
      }
    }

    const updateUsers = () => {
      const now = Date.now();
      const states = awareness.getStates();
      const connectedUsers: UserInfo[] = [];
      const userMap = new Map<string, UserInfo>(); // Deduplicate by user ID
      const activeClientIds = new Set<number>();

      states.forEach((state, clientId) => {
        const user = state.user as { id?: string; name: string; color: string; avatarUrl?: string } | undefined;
        const cleanupMessage = state.cleanupStaleClient as { clientId: number; userId: string; timestamp: number } | undefined;
        
        // Handle cleanup messages
        if (cleanupMessage && cleanupMessage.userId === me?.id) {
          logger.info('Received cleanup message for stale client', cleanupMessage.clientId);
          // We can't directly remove other clients' awareness states, but we can track them as stale
          lastSeenRef.current.delete(cleanupMessage.clientId);
          return; // Skip processing this client
        }
        
        if (user || clientId === localClientId) {
          // Track when we last saw this client
          lastSeenRef.current.set(clientId, now);
          activeClientIds.add(clientId);
          
          const userInfo: UserInfo = {
            clientId,
            name: user?.name || generateUserName(clientId),
            color: user?.color || CURSOR_COLORS[clientId % CURSOR_COLORS.length],
            avatarUrl: user?.avatarUrl,
          };
          
          // If we have a user ID, deduplicate by it (prefer the most recent client ID)
          if (user?.id) {
            const existingUser = userMap.get(user.id);
            if (!existingUser || clientId > existingUser.clientId) {
              userMap.set(user.id, userInfo);
            }
          } else {
            // For users without ID, include them (they'll be deduplicated by client ID)
            connectedUsers.push(userInfo);
          }
        }
      });

      // Clean up stale client IDs (older than 30 seconds)
      const staleThreshold = 30000; // 30 seconds
      for (const [clientId, lastSeen] of lastSeenRef.current.entries()) {
        if (!activeClientIds.has(clientId) && (now - lastSeen) > staleThreshold) {
          lastSeenRef.current.delete(clientId);
        }
      }

      // Add deduplicated users with IDs
      userMap.forEach(userInfo => {
        connectedUsers.push(userInfo);
      });

      setUsers(connectedUsers);
      
      // Track multi-user collaboration (first-time when 2+ users present)
      if (connectedUsers.length >= 2) {
        SessionMetrics.firstTime('collaboration.multi_user');
      }
    };

    // Listen for awareness changes
    awareness.on('change', updateUsers);
    updateUsers(); // Initial update

    // Periodic cleanup of stale awareness states
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 30000; // 30 seconds
      
      // Clean up our tracking of stale client IDs
      for (const [clientId, lastSeen] of lastSeenRef.current.entries()) {
        if ((now - lastSeen) > staleThreshold) {
          lastSeenRef.current.delete(clientId);
        }
      }
    }, 10000); // Check every 10 seconds

    return () => {
      awareness.off('change', updateUsers);
      clearInterval(cleanupInterval);
    };
  }, [awareness, me?.avatarUrl, me?.id, me?.name]);

  if (!awareness) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 px-4">
      {/* User avatars */}
      <div className="flex -space-x-2">
        {users.map((user, index) => {
          const isLocal = user.clientId === localUser?.clientId;
          return (
            <Tooltip key={user.clientId}>
              <TooltipTrigger asChild>
                <div
                  className="relative inline-flex"
                  style={{ zIndex: users.length - index }}
                >
                  {user.avatarUrl ? (
                    <div
                      data-testid="tid.editor.connected-user-avatar"
                      className="w-8 h-8 rounded-full border-[3px] overflow-hidden bg-background shadow-sm"
                      style={{ borderColor: user.color }}
                    >
                      <img
                        src={user.avatarUrl}
                        alt={user.name || 'User'}
                        className="h-full w-full rounded-full object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      data-testid="tid.editor.connected-user-avatar"
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center
                        text-xs font-semibold text-white shadow-sm
                        ${!awareness || !user?.color ? 'bg-muted text-muted-foreground border-2 border-muted-foreground/20 grayscale' : ''}
                      `}
                      style={awareness && user.color ? { backgroundColor: user.color } : undefined}
                    >
                      {user.name ? getInitials(user.name) : 'U'}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {user.name || t('connectedUsers.userFallback', 'User')} {isLocal ? t('connectedUsers.youSuffix', '(You)') : t('connectedUsers.onlineSuffix', '(Online)')}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

    </div>
  );
};
